import { motion } from 'framer-motion'
import { useProjects } from '../hooks/useProjects'
import { GradientBackground } from './GradientBackground'

export function ProjectTypography() {
  const { getRandomProject } = useProjects()
  
  const handleNextProject = () => {
    const randomProject = getRandomProject('typography-architecture')
    if (randomProject) {
      window.location.hash = randomProject.slug
    }
  }
  
  return (
    <GradientBackground>
      {/* Hero Section - Cover image at 65% height */}
      <section className="relative h-[65vh] w-full">
        <img 
          src="/assets/projects/typography-architecture/THUMBNAIL.webp" 
          alt="Typography Architecture"
          className="w-full h-full object-cover object-center"
        />
      </section>

      {/* Project Info */}
      <section className="py-20 px-4" style={{ background: 'linear-gradient(to bottom, transparent 0%, #CFCFCF 100%)' }}>
        <div className="container mx-auto max-w-4xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
          >
            <div>
              <h3 className="text-sm uppercase tracking-wider text-white/60 mb-2">Client</h3>
              <p className="text-lg font-medium text-white">Personal Project</p>
            </div>
            <div>
              <h3 className="text-sm uppercase tracking-wider text-white/60 mb-2">Services</h3>
              <p className="text-lg font-medium text-white">3D Modeling, Motion</p>
            </div>
            <div>
              <h3 className="text-sm uppercase tracking-wider text-white/60 mb-2">Year</h3>
              <p className="text-lg font-medium text-white">2024</p>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">The Concept</h2>
            <p className="text-xl text-white/80 leading-relaxed mb-12">
              A Blender modeling experiment where typography meets architectural space. 
              The bridge structure and articulated geometries explore depth and levels through warm 
              light that defines the path, transforming simple numbers into a three-dimensional visual experience.
            </p>

            {/* Images Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              <div className="space-y-8">
                <img 
                  src="/assets/projects/typography-architecture/Screenshot-2024-12-10-alle-11.10.13.png" 
                  alt="Typography Architecture Render 1"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/projects/typography-architecture/Screenshot-2024-12-10-alle-11.10.34.png" 
                  alt="Typography Architecture Render 2"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/projects/typography-architecture/Screenshot-2024-12-10-alle-11.13.23.png" 
                  alt="Typography Architecture Render 3"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
              <div className="space-y-8">
                <img 
                  src="/assets/projects/typography-architecture/Screenshot-2024-12-10-alle-11.21.44.png" 
                  alt="Typography Architecture Render 4"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/projects/typography-architecture/Screenshot-2024-12-10-alle-11.23.28.png" 
                  alt="Typography Architecture Render 5"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">Technical Approach</h2>
            <p className="text-xl text-white/80 leading-relaxed mb-12">
              Created entirely in Blender using Cycles for rendering. The studied lighting 
              creates a warm and enveloping atmosphere, while camera movement explores 
              different angles to enhance the geometric complexity of the scene.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tools Section */}
      <section className="py-16 px-4 bg-[#CFCFCF]">
        <div className="container mx-auto max-w-4xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h3 className="text-sm uppercase tracking-wider text-white/60 mb-4">Tools Used</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {['Blender', 'Cycles'].map((tool) => (
                <span 
                  key={tool}
                  className="px-6 py-3 bg-white text-white font-medium rounded-lg shadow-sm"
                >
                  {tool}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Projects */}
      <section className="py-20 px-4" style={{ background: 'linear-gradient(to bottom, transparent 0%, #CFCFCF 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/#works"
              className="group relative inline-flex items-center justify-center gap-2 px-8 py-4 text-white text-lg font-medium rounded-lg overflow-hidden cursor-pointer transition-transform duration-300 hover:scale-105" style={{ background: 'linear-gradient(90deg, #7ec876 0%, #4da6ff 50%, #7ec876 100%)', backgroundSize: '200% 100%', animation: 'gradient-shift 3s ease infinite' }}
            >
              ← Back to Works
            </a>
            <button 
              onClick={handleNextProject}
              className="group relative inline-flex items-center justify-center gap-2 px-8 py-4 text-white text-lg font-medium rounded-lg overflow-hidden cursor-pointer transition-transform duration-300 hover:scale-105" style={{ background: 'linear-gradient(90deg, #7ec876 0%, #4da6ff 50%, #7ec876 100%)', backgroundSize: '200% 100%', animation: 'gradient-shift 3s ease infinite' }}
            >
              Next Project →
            </button>
          </div>
        </div>
      </section>

    </GradientBackground>
  )
}
